import React, { useEffect, useState } from 'react';
import { useStore } from '../../../../stores/root';
import moment from 'moment';

// assets
import { faCheck, faFileCheck, faLink, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// services
import ErrorService from '../../../../services/general/error';
import GeneralService from '../../../../services/general';
import EmployeeLeaveService from '../../../../services/employees/leave';
import EmployeeLeaveTypes from '../../../../manifests/types/employeeLeave';

// props
import { IEmployeeLeaveApprovalResourceProps } from '../../../../props/employees/leaves/approval';
import { IEmployeeLeaveBalanceProps } from '../../../../props/employees/leaves/leave';

// components
import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Details from '../../../typography/details';
import SelectEmployee from '../../../uiframeworks/forms/selectEmployee';
import SelectSubsidiary from '../../../uiframeworks/forms/selectSubsidiary';
import Text from '../../../typography/text';
import RenderImage from '../../../uiframeworks/files/renderImage';

interface ApproveEmployeeLeaveFormProps {
    leaveId: string;
    onDismissed(refresh?: boolean, status?: string): void;
}

const ApproveEmployeeLeaveForm: React.FC<ApproveEmployeeLeaveFormProps> = (props: ApproveEmployeeLeaveFormProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [data, setData] = useState<Partial<IEmployeeLeaveApprovalResourceProps>>({
        id: GeneralService.guid()
    });
    const [balance, setBalance] = useState<IEmployeeLeaveBalanceProps | undefined>();

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            const leave = await EmployeeLeaveService.read(props.leaveId);
            setData({ ...data, leave });

            if (leave.employeeBalance) {
                setBalance(leave.employeeBalance);
            } else if (leave.employee?.id) {
                const bal = await EmployeeLeaveService.getBalance(`employeeId=${leave.employee.id}&year=${moment(leave.startDate).year()}`);
                setBalance(bal);
            }

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_leave_approve_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to load request details. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    };

    const _onSubmit = async () => {
        try {
            const confirmed = window.confirm("Approve this leave request? Employee's quota will be automatically deducted.");
            if (confirmed) {
                setSubmitting(true);
                const fd = new FormData();
                fd.append('justification', data.justification || "");

                await EmployeeLeaveService.approve(props.leaveId, fd);

                banner.add({
                    key: 'approve_employee_leave_success',
                    variant: 'success',
                    icon: faCheck,
                    text: `Leave request approved by Director and quota balance deducted.`
                });
                props.onDismissed(true, 'approved');
            }
        } catch (e) {
            setSubmitting(false);
        }
    };

    const leave = data.leave;
    const leaveType = (leave?.type || '').toLowerCase();
    const isAnnual = leaveType.includes('annual') || leaveType.includes('tahunan');
    const isWfh = leaveType.includes('wfh') || leaveType.includes('home');
    const totalDays = Number(leave?.totalDays || 0);

    const usedBefore = isAnnual ? (balance?.annualUsed || 0) : (isWfh ? (balance?.wfhUsed || 0) : 0);
    const quota = isAnnual ? (balance?.annualQuota || 12) : (isWfh ? (balance?.wfhQuota || 3) : 0);
    const remainingBefore = isAnnual ? (balance?.annualRemaining || 0) : (isWfh ? (balance?.wfhRemaining || 0) : 0);
    const remainingAfter = Math.max(0, remainingBefore - totalDays);

    return (
        <Panel
            headerText="Leave Request Approval (Director)"
            isOpen={true}
            type={PanelType.medium}
            onDismiss={() => props.onDismissed(false)}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    {!submitting ? (
                        <>
                            <PrimaryButton text="Approve & Deduct Quota" onClick={_onSubmit} />
                            <DefaultButton text="Close" onClick={() => props.onDismissed(false)} />
                        </>
                    ) : (
                        <Spinner size={SpinnerSize.medium} labelPosition="right" label="Processing approval..." />
                    )}
                </Stack>
            )}
        >
            <Stack tokens={{ childrenGap: 15 }} styles={{ root: { paddingTop: 10 } }}>
                {!loaded ? (
                    <Spinner size={SpinnerSize.medium} labelPosition="right" label="Loading request details..." />
                ) : leave ? (
                    <>
                        {/* Summary Kuota Pemohon */}
                        {balance && (
                            <div style={{
                                background: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: 8,
                                padding: '12px 16px'
                            }}>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                    <FontAwesomeIcon icon={faFileCheck} style={{ color: '#2563eb' }} />
                                    <Text size="small" style={{ fontWeight: 600, color: '#1e40af' }}>
                                        Applicant Quota Summary:
                                    </Text>
                                </Stack>
                                <div style={{ marginTop: 8, fontSize: 13, color: '#1e3a8a', lineHeight: 1.6 }}>
                                    {isAnnual ? (
                                        <>Employee has used <b>{usedBefore}/{quota}</b> day(s) of Annual Leave. This request is for <b>{totalDays}</b> working day(s). Balance after approval: <b>{remainingAfter} day(s)</b>.</>
                                    ) : isWfh ? (
                                        <>Employee has used <b>{usedBefore}/{quota}</b> day(s) of WFH. This request is for <b>{totalDays}</b> working day(s). Balance after approval: <b>{remainingAfter} day(s)</b>.</>
                                    ) : (
                                        <>Employee is requesting <b>{totalDays}</b> day(s) (Special / Non-quota leave).</>
                                    )}
                                </div>
                            </div>
                        )}

                        <SelectEmployee selected={leave.employee} disabled label="Applicant (Employee)" onChange={() => { }} />
                        <SelectSubsidiary selected={leave.subsidiary} disabled label="Subsidiary" onChange={() => { }} />

                        <Stack className="divider"></Stack>
                        <Details text={EmployeeLeaveTypes.find((t) => t.key === leave.type)?.text || leave.type || "-"} title="Leave Type" />
                        <Details title="Date Period" text={`${moment(leave.startDate).format("DD/MM/YYYY")} - ${moment(leave.endDate).format("DD/MM/YYYY")} (${leave.totalDays} Working Day(s))`} />
                        <Details text={leave.reason} title="Reason / Purpose" />

                        {leave.attachmentUrl && (
                            <div style={{ marginTop: 8 }}>
                                <Text size="xsmall" style={{ fontWeight: 600, color: '#64748b' }}>Medical Certificate / Supporting Document:</Text>
                                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {leave.attachmentUrl.startsWith('http') || leave.attachmentUrl.startsWith('data:') ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <img
                                                src={leave.attachmentUrl}
                                                alt="Attachment"
                                                style={{ maxWidth: 220, maxHeight: 150, borderRadius: 6, objectFit: 'contain', border: '1px solid #cbd5e1' }}
                                                onError={(e) => (e.currentTarget.style.display = 'none')}
                                            />
                                            <a href={leave.attachmentUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#0284c7', textDecoration: 'underline', fontSize: 13 }}>
                                                <FontAwesomeIcon icon={faLink} /> Buka Dokumen / Link Penuh
                                            </a>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <div style={{ maxWidth: 220, maxHeight: 160, overflow: 'hidden', borderRadius: 6, border: '1px solid #cbd5e1' }}>
                                                <RenderImage path={leave.attachmentUrl} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <Stack className="divider"></Stack>
                        <TextField
                            label="Director Approval Notes (Optional)"
                            value={data.justification || ''}
                            multiline
                            rows={3}
                            placeholder="Example: Approved."
                            onChange={(evt: any, value?: string) => {
                                setData({ ...data, justification: value || '' });
                            }}
                        />
                    </>
                ) : null}
            </Stack>
        </Panel>
    );
};

export default ApproveEmployeeLeaveForm;
